import { LoadingStateChart } from 'components/analytics';
import { ChartBase } from 'components/common';
import { ExportIcon } from 'components/icons';
import { CardBackground } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import type { PurchaseTiming } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';

const ExportMenuLazy = lazy(() => import('./PurchaseTimingExportMenu'));

interface PurchaseTimingFirst24HoursProps {
  data: PurchaseTiming | undefined;
  loading: boolean;
}

export default function PurchaseTimingFirst24Hours({
  data,
  loading,
}: PurchaseTimingFirst24HoursProps) {
  const exportContainerRef = useRef<HTMLDivElement>(null);

  const { currentStore } = useAppSelector(userDetailsState);

  const currency = currentStore.completeInfo?.currency ?? 'USD';

  /** Start Cards Data */
  const firstHour = data?.firsth ? data?.firsth + '%' : '--';
  const first6Hours = data?.first6h ? data?.first6h.toFixed(2) + '%' : '--';
  const first24Hours = data?.first24h ? data?.first24h + '%' : '--';
  const rev6Hours = data?.rev6h
    ? data?.rev6h.toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : '--';
  const rev24Hours = data?.rev24h
    ? data?.rev24h.toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : '--';
  const revFirstTo24Hours =
    data?.rev6h && data?.rev24h
      ? (data?.rev6h + data?.rev24h).toLocaleString('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : '--';
  // Ends Cards Data

  /** Chart Data Starts */

  // Thresholds is used as reference to slice the dataset into categories and display at different colors

  const minThreshold = 1; // hour
  const maxThreshold = 7;

  // Subsets of data based on the original and threshold settings
  const active = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_timing || {}).filter(
          ([key, _]) => parseInt(key) <= minThreshold
        )
      ),
    [data?.first_order_timing, minThreshold]
  );

  const atRisk = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_timing || {}).filter(
          ([key, _]) =>
            parseInt(key) > minThreshold && parseInt(key) < maxThreshold
        )
      ),
    [data?.first_order_timing, minThreshold, maxThreshold]
  );

  const churn = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.first_order_timing || {}).filter(
          ([key, _]) => parseInt(key) >= maxThreshold
        )
      ),
    [data?.first_order_timing, maxThreshold]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Active',
          backgroundColor: '#4F92F7',
          borderColor: '#4F92F7',
          data: active,
        },
        {
          label: 'At risk',
          backgroundColor: '#F87349',
          borderColor: '#F87349',
          data: atRisk,
        },
        {
          label: 'Churned',
          backgroundColor: '#D5CBB7',
          borderColor: '#D5CBB7',
          data: churn,
        },
      ],
    }),
    [active, atRisk, churn]
  );

  const options = {
    interaction: {
      mode: 'nearest',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          text: 'Hours from signup',
          display: true,
          font: { size: 15 },
        },
        stacked: true,
      },
      y: {
        ticks: {
          callback: (value: number) =>
            (value / 100).toLocaleString('en-US', {
              style: 'percent',
              minimumFractionDigits: value <= 1 ? 2 : 0,
              maximumFractionDigits: value <= 1 ? 2 : 0,
            }),
        },
        title: { text: '% purchased', display: true, font: { size: 15 } },
      },
    },
    plugins: {
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 20,
          font: {
            size: 15,
            lineHeight: '1.5px',
          },
        },
      },
      tooltip: {
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: (tooltipItem: { parsed: { y: string } }) =>
            tooltipItem.parsed.y + '%',
        },
      },
    },
  };

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Hours', key: 'period' },
      { label: 'Active', key: 'active' },
      { label: 'At risk', key: 'at_risk' },
      { label: 'Churn', key: 'churn' },
    ];

    const activeCSV = Object.entries(active).map(([key, value]) => ({
      period: key,
      active: value,
    }));

    const atRiskCSV = Object.entries(atRisk).map(([key, value]) => ({
      period: key,
      at_risk: value,
    }));

    const churnCSV = Object.entries(churn).map(([key, value]) => ({
      period: key,
      churn: value,
    }));

    const csvData = [...activeCSV, ...atRiskCSV, ...churnCSV];

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [active, atRisk, churn]);

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-purchase_timing-first_24_hours-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  if (loading) return <LoadingStateChart chartTitle="First 24 hours" />;
  if (!data?.first_order_timing) return null;

  return (
    <div className="py-6 " ref={exportContainerRef}>
      <div className="flex items-end justify-between px-2">
        <p className="text-lg leading-7 whitespace-nowrap">First 24 hours</p>

        <Suspense
          fallback={
            <div className="flex items-center px-5 py-2.5 space-x-2 rounded-lg bg-segments-gray focus:outline-none">
              <p>Export</p>
              <ExportIcon className="w-5 h-5" />
            </div>
          }
        >
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Suspense>
      </div>

      <div className="mt-3">
        <CardBackground>
          <div className="p-10">
            <div className="flex justify-between">
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">{firstHour}</p>
                <p className="text-base text-black text-opacity-50">
                  Within 1 hour
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">{first6Hours}</p>
                <p className="text-base text-black text-opacity-50">
                  Hours 1-6
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">{first24Hours}</p>
                <p className="text-base text-black text-opacity-50">
                  Hour 6-24
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {revFirstTo24Hours}
                </p>
                <p className="text-base text-black text-opacity-50">
                  $ between hours 1-24
                </p>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-base text-black text-opacity-50">
                <span className="font-bold">{firstHour}</span> of customers
                purchase their first order within an hour of signing up. After
                that, an additional
                <span className="font-bold"> {first6Hours} </span> of orders
                with
                <span className="font-bold"> {rev6Hours} </span>
                sales happen between hours 1 and 6, and
                <span className="font-bold"> {first24Hours} </span> of orders
                with
                <span className="font-bold"> {rev24Hours} </span>sales happen
                between hours 6 and 24.
              </p>
            </div>
            <hr className="my-8" />
            <ChartBase type="bar" data={chartData} options={options} />
          </div>
        </CardBackground>
      </div>
    </div>
  );
}
